import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import "./summary.less";
import { Reit, YearData, PortfolioSummary } from "../../../../types/reit.types";

interface SummaryProps {
  reit: Reit | null;
}

const Summary: React.FC<SummaryProps> = ({ reit }) => {
  if (!reit) return null;

  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>(reit.years[0]?.year || "");
  const [quarter, setQuarter] = useState<string>("");
  const chartRef = useRef<SVGSVGElement | null>(null);

  const selectedYearData = useMemo(
    () => reit.years.find((y) => y.year === year),
    [reit, year]
  );

  const quarterOptions: string[] = useMemo(() => {
    const quarters = (selectedYearData as any)?.quarters || [];
    return quarters.map((q: any) => q.quarter);
  }, [selectedYearData]);

  const currentSummary: PortfolioSummary | undefined = useMemo(() => {
    if (view === "Yearly") {
      return selectedYearData?.portfolioSummary;
    }
    if (view === "Quarterly") {
      const quarters = (selectedYearData as any)?.quarters || [];
      if (!quarters.length) return undefined;
      const qData =
        quarters.find((q: any) => q.quarter === quarter) || quarters[0];
      return qData.portfolioSummary;
    }
    return undefined;
  }, [view, selectedYearData, quarter]);

  const pieData = useMemo(() => {
    if (!currentSummary) return [];

    const completed = Number(currentSummary.completedArea?.value ?? 0);
    const underCons = Number(currentSummary.underConstructionArea?.value ?? 0);
    const proposed = Number(
      currentSummary.proposedFutureDevelopment?.value ?? 0
    );

    const hasStructuredData = completed + underCons + proposed > 0;

    //Case 1: structured numeric values exist then show 3 slices
    if (hasStructuredData) {
      return [
        {
          label: currentSummary.completedArea.key,
          value: completed,
          displayValue: currentSummary.completedArea.value,
          color: "#1B324C",
        },
        {
          label: currentSummary.underConstructionArea.key,
          value: underCons,
          displayValue: currentSummary.underConstructionArea.value,
          color: "#1F70CC",
        },
        {
          label: currentSummary.proposedFutureDevelopment.key,
          value: proposed,
          displayValue: currentSummary.proposedFutureDevelopment.value,
          color: "#A5A5A5",
        },
      ];
    }

    // Case 2: descriptive portfolio string then extract "msf" values and sum
    const rawPortfolio = currentSummary.portfolioSize?.value || "";
    let totalMsf = 0;

    if (typeof rawPortfolio === "string") {
      const matches = rawPortfolio.match(/([\d.]+)\s*msf/gi);
      if (matches) {
        totalMsf = matches
          .map((m) => parseFloat(m))
          .filter((n) => !isNaN(n))
          .reduce((a, b) => a + b, 0);
      }
    }

    return [
      {
        label: "Completed (msf)",
        value: totalMsf,
        displayValue: `${totalMsf.toFixed(1)} msf`,
        color: "#1B324C",
      },
    ];
  }, [currentSummary]);

  useEffect(() => {
    if (view === "Quarterly") {
      const quarters = (selectedYearData as any)?.quarters || [];
      if (quarters.length) {
        // if current quarter doesn’t exist in the new year, reset to first quarter
        const found = quarters.find((q: any) => q.quarter === quarter);
        if (!found) {
          setQuarter(quarters[0].quarter);
        }
      } else {
        setQuarter("");
      }
    }
  }, [year, view, selectedYearData]);

  useEffect(() => {
    if (!chartRef.current || pieData.length === 0) return;

    const svgElement = chartRef.current;
    const container = svgElement.parentElement;
    if (!container) return; // ✅ avoids 'container is possibly null'

    const containerWidth = container.offsetWidth || 240;
    const containerHeight = container.offsetHeight || 240;

    const width = containerWidth;
    const height = containerHeight;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(svgElement);
    svg.selectAll("*").remove();

    svg.attr("width", width).attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const pie = d3
      .pie<{
        label?: string;
        value: number;
        displayValue?: string | number | boolean;
        color: string;
      }>()
      .sort(null)
      .value((d) => d.value);

    const arc = d3
      .arc<d3.PieArcDatum<{ label: string; value: number; color: string }>>()
      .innerRadius(radius * 0.58)
      .outerRadius(radius * 0.92);

    // draw slices
    const arcs = g
      .selectAll("path.slice")
      .data(
        pie(
          pieData.map((d) => ({
            ...d,
            label: d.label ?? "", 
          }))
        )
      )
      .enter()
      .append("path")
      .attr("class", "slice")
      .attr("d", arc as any)
      .attr("fill", (d) => d.data.color)
      .style("opacity", 0);

    arcs
      .transition()
      .duration(700)
      .delay((_, i) => i * 120)
      .style("opacity", 1);

    // center text (Year or Quarter)
    const centerText = view === "Yearly" ? `FY ${year}` : quarter || "";
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.2em")
      .style("font-size", "16px")
      .style("font-weight", 700)
      .style("fill", "#3B3B3B")
      .style("font-family", "'Red Hat Text', sans-serif")
      .text(centerText);
  }, [pieData, view, year, quarter]);

  return (
    <section className="summary">
      <div className="summary__card">
        {/* Header */}
        <div className="summary__header">
          <h2>Portfolio Summary</h2>

          <div className="summary__subheader">
            <div className="summary__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => {
                  setView("Quarterly");
                  if (quarterOptions.length) setQuarter(quarterOptions[0]);
                }}
              >
                Quarterly
              </button>
            </div>

            <div className="summary__dropdowns">
              {view === "Quarterly" && quarterOptions.length > 0 && (
                <div className="summary__dropdown" style={{ marginRight: 8 }}>
                  <div className="custom-select">
                    <select
                      value={quarter}
                      onChange={(e) => setQuarter(e.target.value)}
                    >
                      {quarterOptions.map((q, idx) => (
                        <option key={idx} value={q}>
                          {q}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="summary__dropdown">
                <div className="custom-select">
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {reit.years.map((y, idx) => (
                      <option key={idx} value={y.year}>
                        FY {y.year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="summary__body">
          <div className="summary__chart-box">
            <div className="summary__chart-area">
              <svg ref={chartRef} />
            </div>

            <div className="summary__legend">
              <div className="legend-item no-dot">
                <div className="legend-text">
                  <div className="legend-label">
                    {currentSummary?.portfolioSize?.key}
                  </div>
                  <div className="legend-value">
                    {currentSummary?.portfolioSize?.value}
                  </div>
                </div>
              </div>

              {pieData.map((p, i) => (
                <div className="legend-item" key={i}>
                  <span
                    className="legend-dot"
                    style={{ background: p.color }}
                    aria-hidden
                  />
                  <div className="legend-text">
                    <div className="legend-label">{p.label}</div>
                    <div className="legend-value">{p.displayValue}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="summary__table">
            <table>
              <thead>
                <tr>
                  <th>Year</th>
                  <th>{view === "Yearly" ? `FY ${year}` : quarter}</th>
                </tr>
              </thead>
              <tbody>
                {currentSummary?.portfolioSize && (
                  <tr>
                    <td>{currentSummary.portfolioSize.key}</td>
                    <td>{currentSummary.portfolioSize.value}</td>
                  </tr>
                )}

                {pieData.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.label}</td>
                    <td>{row.displayValue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Summary;
